import React, {useEffect, useState} from 'react';
import {
    AddButton,
    ContentWrapper,
    DialogEditActionsWrapper,
    Locales,
    LocalesLabelWrapper,
    LocalesSelectionWrapper,
    ParentLocalesWrapper,
    ResetButton
} from './AdvancedLocalePicker.style';
import {t} from 'i18next';
import {dynamicConfig} from '../../init/config';
import {countryResource} from './resources/CountryResource';
import {languageResource} from './resources/LanguageResource';
import {countryLanguageResource} from './resources/CountryLanguageResource';
import {CountryPicker} from './CountryPicker';
import {LanguagePicker} from './LanguagePicker';
import {ParentLocales} from './ParentLocales';
import EventEmitter from 'events';
import {AdvancedLocalePicker_addAllLocales} from '../../init/events';
import {isNullOrEmpty} from '../../utils';

export interface LocalesSelectionProps {
    readonly defaultLocale: string;
    readonly locales: string[];
    readonly parentLocales: string[];
    readonly localesToDelete: string[];
    readonly setParentLocales: boolean;
    setLocalesToDelete: (localesToDelete: string[]) => void;
    eventEmitter: EventEmitter;
    onChange: (changedLocales: string[]) => void;
    onReset: () => void;
}

const getLocaleLabel = (locale: string) => {
    let localeLabel = '';
    let labelFormat = locale.split('_');
    if (labelFormat.length > 2) {
        return localeLabel;
    }

    let language = labelFormat[0];
    let country = labelFormat[1];
    if ((!isNullOrEmpty(language)) && (!isNullOrEmpty(country))) {
        localeLabel = `/${country.toLowerCase()}/${language.toLowerCase()}`;
    }

    return localeLabel;
};

const countryRestriction: string[] = dynamicConfig.DLS_COUNTRIES;
const languageRestriction: string[] = dynamicConfig.DLS_LANGUAGES;

const getCountryDataSource = () => {
    return countryResource
        .filter(country => ((countryRestriction.length === 0)
            || ((countryRestriction.length > 0) && (countryRestriction.includes(country.value)))))
        .map(country => country.value);
};

const createLocale = (languageSelection: string, countrySelection: string) => {
    return `${languageSelection.toLowerCase()}_${countrySelection.toUpperCase()}`;
};

const filterLocales = (locales: string[]) => {
    let filteredLocales: string[] = [];
    let localeItem: string[] = [];
    if ((locales) && (locales.length > 0)) {
        filteredLocales = locales
            .map(locale => locale)
            .filter(locale => {
                localeItem = locale.split('_');
                if (localeItem.length >= 2) {
                    return (((countryRestriction.length === 0)
                            || ((countryRestriction.length > 0) && (countryRestriction.includes(localeItem[1].toUpperCase()))))
                        && ((languageRestriction.length === 0)
                            || ((languageRestriction.length > 0) && (languageRestriction.includes(localeItem[0])))))
                }
                return false;
            });
    }

    return filteredLocales;
};

/**
 * @name AdvancedLocalePicker
 * @description
 * Component to make a selection of string tags that can describe an item
 */
export const AdvancedLocalePicker: React.VFC<LocalesSelectionProps> = (props) => {
    const getCountryLanguageMap = (): Map<string, string[]> => {
        let countryLanguageScope = new Map<string, string[]>();
        let parentLocales = ((props.parentLocales) && (props.parentLocales.length > 0)) ? props.parentLocales : [];
        let localeData: string[] = [];
        let countryKey = '';
        let language = '';
        let languages: string[] = [];
        parentLocales
            .map(locale =>
                locale.split(',')
                    .map(localeItem => {
                        localeData = localeItem.split('_');
                        language = localeData[0].toLowerCase();
                        countryKey = localeData[1].toUpperCase();
                        languages = [];
                        let tempLanguages = countryLanguageScope.get(countryKey);
                        if (tempLanguages) {
                            languages = tempLanguages;
                        }
                        if ((!languages.includes(locale))
                            && ((languageRestriction.length === 0)
                                || ((languageRestriction.length > 0) && (languageRestriction.includes(language))))) {
                            languages = languages.concat(language);
                        }
                        countryLanguageScope.set(countryKey, languages);
                    })
            );
        if (countryLanguageScope.size === 0) {
            countryLanguageResource.map(countryLanguage =>
                countryLanguageScope
                    .set(countryLanguage.country,
                        countryLanguageResource
                            .filter(countryLanguageItem => (countryLanguage.country === countryLanguageItem.country))
                            .filter(countryLanguageItem => ((languageRestriction.length === 0)
                                || ((languageRestriction.length > 0) && (languageRestriction.includes(countryLanguageItem.language)))))
                            .map(countryLanguageItem => countryLanguageItem.language)));
        }

        return countryLanguageScope;
    };

    const getCountryOptions = () => {
        return getCountryDataSource().filter(country => (countryLanguageScope.has(country)));
    };

    const getCountryLanguageScope = (countryLanguageScope: Map<string, string[]>, countrySelectionState: string) => {
        let countryLanguages: string[] = [];
        let tempCountryLanguages = countryLanguageScope.get(countrySelectionState);
        if (tempCountryLanguages) {
            countryLanguages = tempCountryLanguages;
        }

        return countryLanguages;
    };

    const getLanguageOptions = (countrySelectionState: string) => {
        let countryLanguages = getCountryLanguageScope(countryLanguageScope, countrySelectionState);
        return languageResource.filter(lang => (
            countryLanguageResource
                .filter(countryLanguage => (countryLanguage.country === countrySelectionState))
                .map(countryLanguage => countryLanguage.language))
            .includes(lang.value))
            .map(language => language.value)
            .filter(language => (countryLanguages.includes(language)));
    };

    const [localeOptions, setLocaleOptions] = useState<string[]>(props.locales ? props.locales : [props.defaultLocale]);
    const getLocales = () => {
        let locales: string[] = [];
        if ((localeOptions) && (localeOptions.length > 0)) {
            locales = filterLocales(localeOptions);
        }

        return locales;
    };

    const getParentLocaleDatasource = () => {
        let parentLocalesResult: string[] = [];
        if ((props.parentLocales) && (props.parentLocales.length > 0)) {
            parentLocalesResult = filterLocales(props.parentLocales);
        }
        if (parentLocalesResult.length === 0) {
            let locales: string[] = [];
            countryLanguageScope.forEach((value, key) => {
                value.map(language => {
                    if ((language.trim().length > 0) && (key.trim().length > 0)) {
                        locales.push(createLocale(language, key))
                    }
                });
                return locales;
            });
            parentLocalesResult = filterLocales(locales);
        }

        return parentLocalesResult;
    };

    const [localesInitialState] = useState(filterLocales(localeOptions));
    const [countryInput, setCountryInput] = useState('');
    const [languageInput, setLanguageInput] = useState('');
    const [languageReset, setLanguageReset] = useState<boolean>(false);
    const [disableLanguage, setDisableLanguage] = useState<boolean>(true);
    const [disableAddLocale, setDisableAddLocale] = useState<boolean>(true);
    const [countryLanguageScope] = useState<Map<string, string[]>>(getCountryLanguageMap());
    const [languageOptions, setLanguageOptions] = useState<string[]>([]);
    const [protectedLocales] = useState(localeOptions.filter(locale => (!localesInitialState.includes(locale))));
    const [parentLocales] = useState(getParentLocaleDatasource());

    useEffect(() => {
        props.setLocalesToDelete([]);
    }, []);

    useEffect(() => {
        setLocaleOptions(props.locales ? props.locales : [props.defaultLocale])
        handleCountryInput([]);
        handleLanguageInput([]);
    }, [props.locales]);

    const handleCountryInput = (countrySelection: string[]) => {
        setCountryInput(countrySelection[0]);
        setLanguageReset(true);
        setLanguageOptions(getLanguageOptions(countrySelection[0]));
        setDisableAddLocale(getDisableAddLocale(countrySelection[0], languageInput));
        setDisableLanguage(!!(isNullOrEmpty(countrySelection[0])));
    };

    const handleLanguageInput = (languageSelection: string[]) => {
        setLanguageInput(languageSelection[0]);
        setDisableAddLocale(getDisableAddLocale(countryInput, languageSelection[0]));
        setLanguageReset(false);
    };

    const getItemLocales = (selectedLocales: string[]) => {
        if ((selectedLocales && (selectedLocales.length > 0))) {
            let compensatedLocales = protectedLocales
                .filter(locale => (!selectedLocales.includes(locale)));
            return selectedLocales.concat(compensatedLocales);
        }

        if ((selectedLocales && (selectedLocales.length === 0))) {
            selectedLocales = selectedLocales.concat(props.defaultLocale);
        }

        return protectedLocales.concat(selectedLocales);
    };

    const saveLocales = () => {
        let locale = createLocale(languageInput, countryInput);
        let selectedLocales = getLocales();
        if (!selectedLocales.includes(locale)) {
            selectedLocales = selectedLocales.concat(locale);
        }

        if (selectedLocales.length === 0) {
            selectedLocales = selectedLocales.concat(props.defaultLocale);
        }

        props.setLocalesToDelete(props.localesToDelete.filter(localeToDelete => (localeToDelete !== locale)));
        props.onChange(getItemLocales(selectedLocales));
        setLanguageReset(true);
        setDisableAddLocale(true);
    };

    const saveAllLocales = () => {
        props.setLocalesToDelete([]);
        props.onChange(getItemLocales(parentLocales));
        setLanguageReset(true);
        setDisableAddLocale(true);
    };

    props.eventEmitter.on(AdvancedLocalePicker_addAllLocales, saveAllLocales);

    const resetSelectedLocales = () => {
        props.setLocalesToDelete(localesInitialState
            .filter(locale => (locale !== props.defaultLocale))
            .filter(locale => (!protectedLocales.includes(locale))));
        props.onReset();
        setLanguageReset(true);
        setDisableAddLocale(true);
    };

    const getDisableAddLocale = (countrySelection: string, languageSelection: string) => {
        if ((isNullOrEmpty(countrySelection)) || (isNullOrEmpty(languageSelection))) {
            return true;
        }

        let countryLanguages = getCountryLanguageScope(countryLanguageScope, countrySelection);
        if ((!countryLanguages.includes(languageSelection))) {
            return true;
        }

        let locale = createLocale(languageSelection, countrySelection);

        return getLocales().includes(locale);
    };

    const handleDelete = (localeKey: string) => {
        if ((!!(props.defaultLocale !== localeKey)) && (!props.localesToDelete.includes(localeKey))) {
            props.setLocalesToDelete([...props.localesToDelete, localeKey]);
        }
        props.onChange(getItemLocales(getLocales().filter(locale => (locale !== localeKey))));
    };

    const getParentLocaleLabels = (parentLocales: string[]) => {
        return parentLocales.map(locale => getLocaleLabel(locale));
    };

    return (
        <LocalesSelectionWrapper>
            <ContentWrapper>
                <ParentLocalesWrapper>
                    <ParentLocales key={'parentLocales'}
                                   parentLocales={getParentLocaleLabels(parentLocales)}/>
                </ParentLocalesWrapper>
            </ContentWrapper>
            <CountryPicker dataSource={getCountryOptions()}
                           defaultValue={''}
                           resetState={false}
                           resetInput={false}
                           disabled={false}
                           onChange={(countrySelection: string[]) => handleCountryInput(countrySelection)}
            />
            <LanguagePicker dataSource={languageOptions}
                            defaultValue={''}
                            resetState={languageReset}
                            resetInput={false}
                            disabled={disableLanguage}
                            onChange={(languageSelection: string[]) => handleLanguageInput(languageSelection)}
            />
            <DialogEditActionsWrapper>
                <AddButton
                    key='add'
                    icon='add'
                    mode='primary'
                    disabled={disableAddLocale}
                    type='button'
                    label={t('TAG_SELECTION_COMPONENT_ADD_LABEL')}
                    onClick={saveLocales}
                />
                <AddButton
                    key='addAll'
                    icon='add'
                    mode='secondary'
                    disabled={false}
                    type='button'
                    label={t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL')}
                    onClick={saveAllLocales}
                />
                <ResetButton
                    key='resetSelection'
                    icon='reset'
                    mode='secondary'
                    disabled={false}
                    type='button'
                    label={t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')}
                    onClick={resetSelectedLocales}
                />
            </DialogEditActionsWrapper>
            <LocalesLabelWrapper>
                {getLocales().map(locale => {
                        if (!!(localeOptions.length > 1)) {
                            return <Locales key={locale}
                                            label={getLocaleLabel(locale)}
                                            buttonClose
                                            onClose={() => handleDelete(locale)}/>
                        } else {
                            return <Locales key={locale}
                                            label={getLocaleLabel(locale)}
                                            disabled={true}
                            />
                        }
                    }
                )}
            </LocalesLabelWrapper>
        </LocalesSelectionWrapper>
    );
};
